import {Alert, Tag, Typography} from "antd";
import type {MelangeDistributionResult} from "@/raids/rest/RaidSchema.ts";
import {formatDateTime} from "@/helpers/dateFormatHelper.ts";
import {localizedNumberFormatter} from "@/helpers/numberFormatHelper.tsx";
import {IconText} from "@/components/IconText.tsx";
import Spice from "@/assets/resources/spice-sand.webp";

type Props = {
  result: MelangeDistributionResult;
};

export function MelangePreviewResult({result}: Props) {
  const fmt = (n: number) => localizedNumberFormatter(n);

  return (
    <div className="flex flex-col gap-3 border-0 border-t border-solid border-[var(--ant-color-border-secondary)] pt-4">
      <Typography.Text strong>Ergebnis</Typography.Text>
      {result.allParticipationWeightsZero && (
        <Alert
          type="warning"
          showIcon
          message="Keine Teilnahmezeit im Raid-Fenster"
          description="Die Verteilung folgt der Fair-Distributor-Logik; bei 0 aktiven Minuten für alle sind die Anteile nicht sinnvoll gewichtet."
        />
      )}
      <div className="flex flex-wrap gap-2 text-sm">
        <Tag>Umwandlungen: {fmt(result.conversionRuns)}</Tag>
        <Tag>Sand/Lauf: {fmt(result.sandPerRun)}</Tag>
        <Tag>Verbraucht: {fmt(result.sandConsumed)}</Tag>
        <Tag>Rest-Sand gesamt: {fmt(result.remainingSand)}</Tag>
        <Tag color="purple">Melange gesamt: {fmt(result.totalMelange)}</Tag>
        <Tag color="volcano">Rückstände gesamt: {fmt(result.totalSpiceResidue)}</Tag>
        <Tag bordered={false} className="text-[var(--ant-color-text-secondary)]!">
          Berechnung bis {formatDateTime(result.effectiveEndTime)}
        </Tag>
      </div>
      <Typography.Text type="secondary" className="text-xs!">
        Anteile pro Teilnehmer (nach Zeit im Raid, wie Fair-Verteilung)
      </Typography.Text>
      <ul className="list-none m-0 p-0 flex flex-col gap-2">
        {result.participants.map((row) => (
          <li
            key={row.participantId}
            className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 sm:items-center w-full min-w-0 border-0 border-b border-solid border-[var(--ant-color-border-secondary)] pb-2 last:border-b-0"
          >
            <div className="flex flex-wrap gap-2 items-center min-w-0">
              <span className="font-medium truncate max-w-full">{row.name ?? `ID ${row.participantId}`}</span>
              <Tag color="processing">{fmt(row.activeMinutesInRaid)} Minuten</Tag>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <IconText image={Spice} text={fmt(row.spiceSand)} />
              <Tag color="purple">Melange: {fmt(row.melange)}</Tag>
              <Tag color="volcano">Rückstände: {fmt(row.spiceResidue)}</Tag>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
